
import React from "react";
import { Truck, Clock, Award, CheckCircle } from "lucide-react";

interface DeptSpecific {
  name: string;
  code: string;
  cities: string[];
}

interface HeroSectionProps {
  deptSpecific?: DeptSpecific;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  deptSpecific
}) => {
  const scrollToForm = () => {
    const formElement = document.getElementById('request-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative bg-gradient-to-br from-fabre-green via-fabre-teal to-fabre-darkgreen text-white py-20 md:py-28 overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-32 h-32 border border-white/20 rounded-full"></div>
        <div className="absolute top-32 right-20 w-24 h-24 border border-white/20 rounded-full"></div>
        <div className="absolute bottom-20 left-1/4 w-16 h-16 border border-white/20 rounded-full"></div>
        <div className="absolute bottom-32 right-1/3 w-20 h-20 border border-white/20 rounded-full"></div>
      </div>

      <div className="relative container mx-auto px-4">
        <div className="max-w-5xl mx-auto text-center">
          {/* Icon header */}
          <div className="flex justify-center mb-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-full p-4">
              <Truck className="w-12 h-12 text-white" />
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-6 leading-tight">
            {deptSpecific ? `Location de bennes ${deptSpecific.name} (${deptSpecific.code})` : "Location de Bennes"}
            <span className="block text-3xl sm:text-4xl lg:text-5xl mt-2 text-fabre-lightgreen">
              Professionnelle
            </span>
          </h1>
          
          <div className="w-24 h-1.5 bg-white/30 mx-auto mb-8 rounded-full"></div>
          
          <p className="text-xl md:text-2xl opacity-90 leading-relaxed mb-12 max-w-4xl mx-auto">
            {deptSpecific 
              ? `Solutions professionnelles de location de bennes dans le département ${deptSpecific.name}. Service disponible à ${deptSpecific.cities.slice(0, 3).join(', ')} et dans toute la région.`
              : "Solutions professionnelles pour particuliers, entreprises et collectivités. Livraison rapide 24-48h, tarifs transparents dès 120€."
            }
          </p>

          {/* Key benefits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/15 transition-all duration-300">
              <Clock className="w-6 h-6 text-white mx-auto mb-2" />
              <div className="text-sm font-medium">Livraison 24-48h</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/15 transition-all duration-300">
              <Award className="w-6 h-6 text-white mx-auto mb-2" />
              <div className="text-sm font-medium">25 ans d'expérience</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/15 transition-all duration-300">
              <CheckCircle className="w-6 h-6 text-white mx-auto mb-2" />
              <div className="text-sm font-medium">Devis gratuit</div>
            </div>
          </div>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={scrollToForm}
              className="bg-white text-fabre-green px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Devis gratuit en 2 minutes
            </button>
            <a 
              href="tel:+33565787900"
              className="bg-fabre-lightgreen text-fabre-darkgreen px-8 py-4 rounded-xl font-bold text-lg hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              05 65 78 79 00
            </a>
          </div>
          
          {deptSpecific && (
            <div className="mt-8 text-sm opacity-90 bg-white/10 backdrop-blur-sm rounded-lg p-4 max-w-md mx-auto">
              <p>Service disponible dans tout le département {deptSpecific.name} ({deptSpecific.code})</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
